import { makeAutoObservable } from 'mobx'

import type { PlayerHandle, PlayerStatus } from './types'

const SOURCE_TRANSITION_TOLERANCE_SECONDS = 0.3

type SourceTransition = {
  pendingDuration: number | null
  seekIssued: boolean
  targetTime: number
}

// per-instance 播放运行时状态：每个 PlayerProvider 持有一个独立实例。
// 命令（下行）只委派给 attach 进来的句柄、不直接写状态；状态一律由
// handleXxx 回写（上行）写入，使底层 <video> 成为唯一事实来源。
class PlayerStore {
  status: PlayerStatus = 'idle'
  duration = 0
  currentTime = 0
  volume = 1
  muted = false
  videoSize: { width: number; height: number } = { width: 0, height: 0 }
  // 缓冲中（如 seek 到未缓冲位置触发 waiting）：与 status 解耦的瞬时信号。
  // 不写进 status，是为了让 isPlaying（驱动播放/暂停按钮）只反映播放意图、
  // 不被 seek 引起的短暂缓冲翻成「暂停态」——避免快进/快退时按钮抖动（中止→播放）。
  // 单独用它驱动加载中转圈提示。
  buffering = false
  // 仅由用户明确的播放/暂停操作递增。临时暂停、拖动恢复和换源续播据此避免覆盖新意图。
  private _playbackIntentVersion = 0

  // 命令式句柄：由胶水层挂载后 attach、卸载时置空；非 observable。
  _handle: PlayerHandle | null = null

  // 换源时底层会短暂回报 currentTime/duration=0；在 seek 回原位置前暂存这些事件，避免 UI 闪回原点。
  private _sourceTransition: SourceTransition | null = null

  // HTML video 的 currentTime/timeupdate 只代表播放头已移动，不代表目标帧已呈现。
  // seek 期间保持 buffering，直到底层确认新帧已送入合成器。
  private _awaitingPresentedFrame = false

  constructor() {
    makeAutoObservable(this, { _handle: false }, { autoBind: true })
  }

  attachHandle(handle: PlayerHandle | null) {
    this._handle = handle
  }

  // 命令（下行）：委派句柄；句柄为空时安全 no-op（守卫挂载前 / 卸载后竞态）。
  play() {
    this._handle?.play()
  }

  pause() {
    this._handle?.pause()
  }

  togglePlay() {
    if (this.isPlaying) {
      this.pause()
    } else {
      this.play()
    }
  }

  playByUser() {
    this._playbackIntentVersion += 1
    this.play()
  }

  pauseByUser() {
    this._playbackIntentVersion += 1
    this.pause()
  }

  togglePlayByUser() {
    this._playbackIntentVersion += 1
    this.togglePlay()
  }

  seek(time: number) {
    this._handle?.seek(time)
  }

  beginSourceTransition(targetTime: number) {
    if (this._sourceTransition) return
    this._sourceTransition = {
      pendingDuration: null,
      seekIssued: false,
      targetTime,
    }
    this.buffering = true
  }

  resumeSourceTransition() {
    const transition = this._sourceTransition
    if (!transition) return
    if (transition.pendingDuration !== null) {
      transition.targetTime = Math.min(transition.targetTime, transition.pendingDuration)
    }
    transition.seekIssued = true
    this.buffering = true
    this.seek(transition.targetTime)
  }

  cancelSourceTransition() {
    this._sourceTransition = null
    this._awaitingPresentedFrame = false
    this.buffering = false
  }

  setVolume(volume: number) {
    this._handle?.setVolume(volume)
  }

  setMuted(muted: boolean) {
    this._handle?.setMuted(muted)
  }

  // 倍速（下行）：委派句柄写 video.playbackRate；用于长按快进。rAF 外推读 el.playbackRate 自动反映。
  setPlaybackRate(rate: number) {
    this._handle?.setPlaybackRate(rate)
  }

  // 回写（上行）：由胶水层在底层事件回调里调用。
  handleCanPlay(time: number) {
    this.handleTimeUpdate(time)
    if (!this._sourceTransition) this.buffering = false
  }

  handlePlay() {
    this.status = 'playing'
  }

  handlePlaying() {
    this.status = 'playing'
    if (!this._sourceTransition && !this._awaitingPresentedFrame) this.buffering = false
  }

  handlePause() {
    this.status = 'paused'
    if (!this._sourceTransition && !this._awaitingPresentedFrame) this.buffering = false
  }

  handleSeeking() {
    this._awaitingPresentedFrame = true
    this.buffering = true
  }

  handleSeeked(time: number) {
    this.handleTimeUpdate(time)
    if (!this._sourceTransition) this.buffering = false
  }

  // 仅标记缓冲，不改 status：seek 到未缓冲位置时不让播放/暂停按钮翻成暂停态。
  handleWaiting() {
    this.buffering = true
  }

  handleEnded() {
    this.status = 'ended'
    this._awaitingPresentedFrame = false
    if (!this._sourceTransition) this.buffering = false
  }

  handleTimeUpdate(time: number) {
    const transition = this._sourceTransition
    if (transition) {
      if (!transition.seekIssued || Math.abs(time - transition.targetTime) > SOURCE_TRANSITION_TOLERANCE_SECONDS) {
        return
      }
      if (transition.pendingDuration !== null) {
        this.duration = transition.pendingDuration
      }
      this._sourceTransition = null
    }
    this.currentTime = time
    // HTML video seek 中的 timeupdate 不能证明目标帧已经呈现；其他后端仍沿用时间回写清理 buffering。
    if (this.buffering && !this._awaitingPresentedFrame) this.buffering = false
  }

  handleFramePresented(time: number) {
    this._awaitingPresentedFrame = false
    this.handleTimeUpdate(time)
    if (!this._sourceTransition) this.buffering = false
  }

  handleFramePresentationAborted(time: number) {
    this._awaitingPresentedFrame = false
    this.handleTimeUpdate(time)
    if (!this._sourceTransition) this.buffering = false
  }

  handleDurationChange(duration: number) {
    if (this._sourceTransition) {
      if (Number.isFinite(duration) && duration > 0) {
        this._sourceTransition.pendingDuration = duration
        this._sourceTransition.targetTime = Math.min(this._sourceTransition.targetTime, duration)
      }
      return
    }
    this.duration = duration
  }

  handleVolumeChange(volume: number, muted: boolean) {
    this.volume = volume
    this.muted = muted
  }

  handleLoadedMetadata(size: { width: number; height: number }) {
    this.videoSize = size
  }

  handleError(error: unknown) {
    console.error('[PlayerStore] media error', error)
    this.status = 'error'
    this._awaitingPresentedFrame = false
    if (!this._sourceTransition) this.buffering = false
  }

  // 派生（getter → mobx computed）。
  get isPlaying() {
    return this.status === 'playing'
  }

  get isPaused() {
    return this.status === 'paused'
  }

  get playbackIntentVersion() {
    return this._playbackIntentVersion
  }

  get progress() {
    if (this.duration <= 0) {
      return 0
    }
    return Math.min(1, this.currentTime / this.duration)
  }
}

export { PlayerStore }
